// MainNav — sticky header: wordmark, the six sections (Desk · Videos · Photos ·
// Runs · Quality · Settings), the theme toggle and the always-visible
// content-pipeline switch. No drawer: below `lg` the section labels collapse to
// icons (the label stays in the accessible name), and below `sm` the sections
// take a row of their own under the wordmark and the switches (six icons and
// "Pipeline paused" do not fit one 375 px row).

import { Link } from '@tanstack/react-router';
import { Icon } from '@/components/Icon';
import { cn } from '@/lib/cn';
import { ThemeToggleButton } from '@/theme/ThemeToggle';
import { PipelineSwitch } from './PipelineSwitch';
import { SECTIONS } from './navItems';

const LINK_BASE =
  'inline-flex min-h-9 shrink-0 items-center gap-2 whitespace-nowrap rounded px-2 py-1.5 text-sm font-medium ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus ' +
  'focus-visible:ring-offset-2 focus-visible:ring-offset-surface-0';

export function MainNav(): JSX.Element {
  return (
    <header
      className="sticky top-0 z-overlay border-b border-border-subtle bg-surface-0/95 backdrop-blur"
      data-testid="main-nav"
    >
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 pb-2 pl-[max(1rem,env(safe-area-inset-left))] pr-[max(1rem,env(safe-area-inset-right))] pt-[max(0.5rem,env(safe-area-inset-top))]">
        <span className="shrink-0 pr-2 text-sm font-semibold tracking-wide text-fg-default">
          Studio
        </span>
        <nav
          aria-label="Main"
          className="order-last flex w-full items-center justify-between gap-1 sm:order-none sm:w-auto sm:min-w-0 sm:flex-1 sm:justify-start"
        >
          {SECTIONS.map((section) => (
            <Link
              key={section.to}
              to={section.to}
              className={cn(LINK_BASE, 'text-fg-muted hover:bg-overlay-hover hover:text-fg-default')}
              activeProps={{
                className: cn(LINK_BASE, 'border-b-2 border-accent-9 bg-surface-1 text-fg-default'),
                'aria-current': 'page',
              }}
            >
              <Icon icon={section.icon} size={16} />
              <span className="sr-only lg:not-sr-only">{section.label}</span>
            </Link>
          ))}
        </nav>
        <div className="ml-auto flex shrink-0 items-center gap-2">
          <ThemeToggleButton />
          <PipelineSwitch />
        </div>
      </div>
    </header>
  );
}
